/**
 * Integrations — how Trinetra sits alongside a customer's existing VMS and analytics.
 *
 * FRONT-END ONLY. There is no connector service behind this page yet: connections,
 * tests and imports are staged in component state so the shape of the integration can
 * be shown and agreed before any of it is built. Everything resets on reload.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTabs from '../components/common/PageTabs';
import StatusTag from '../components/common/StatusTag';
import { PageTitle } from '../components/layout/TitleBar';
import { usePermissions } from '../auth';
import ConnectorGrid from '../components/integrations/ConnectorGrid';
import CameraImport from '../components/integrations/CameraImport';
import EventBridge from '../components/integrations/EventBridge';
import {
  VMS_CONNECTORS,
  ANALYTICS_CONNECTORS,
  type ConnectionState,
} from '../components/integrations/connectors';
import {
  ArrowLeftIcon,
  PuzzlePieceIcon,
  LinkIcon,
  ArrowDownOnSquareIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

/** The three steps every one of these integrations follows, whatever the vendor. */
const STEPS = [
  { icon: LinkIcon, title: 'Point at the system', body: 'Server address and a read account — nothing is moved or reconfigured on their side.' },
  { icon: ArrowDownOnSquareIcon, title: 'Adopt the cameras', body: 'Trinetra reads the inventory and mirrors it, disabled, until someone reviews it.' },
  { icon: BoltIcon, title: 'Run analytics on top', body: 'Detections flow into Trinetra, and back out to whatever the team already watches.' },
];

export interface IntegrationsPageProps {
  /**
   * Where the Settings page this was opened from lives. Both apps route it at `/settings`
   * today; it is a prop so neither app has the other's route spelled into a shared file.
   */
  backTo?: string;
  /**
   * Named in the header, so a cloud operator can see WHICH site they are describing an
   * integration for. Omitted on the local node, where there is only ever one.
   */
  siteName?: string | null;
  /** This app's real camera importer, if it has one — see CameraImport's own note. */
  bulkUploadPath?: string;
}

export default function IntegrationsPage({
  backTo = '/settings',
  siteName = null,
  bulkUploadPath,
}: IntegrationsPageProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('settings.edit');

  const [tab, setTab] = useState('vms');
  const [connections, setConnections] = useState<Record<string, ConnectionState>>({});

  const connectedCount = Object.values(connections).filter((c) => c.status === 'connected').length;

  function setConnection(id: string, next: ConnectionState) {
    setConnections((prev) => ({ ...prev, [id]: next }));
  }

  const tabs = [
    { key: 'vms', label: 'VMS Connectors', count: VMS_CONNECTORS.length },
    { key: 'import', label: 'Camera Import' },
    { key: 'analytics', label: 'Analytics Modules', count: ANALYTICS_CONNECTORS.length },
  ];

  return (
    <div className="space-y-3">
      <PageTitle title={siteName ? `${siteName} integrations` : 'Integrations'}>
        <StatusTag tone="info">Preview</StatusTag>
        {connectedCount > 0 && <StatusTag tone="success">{connectedCount} connected</StatusTag>}
        <Link
          to={backTo}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" /> Settings
        </Link>
      </PageTitle>

      {/* Hero — this page is shown to customers, so it opens with the claim it makes. */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand text-white">
            <PuzzlePieceIcon className="h-5 w-5" />
          </div>
          <p className="min-w-0 max-w-3xl flex-1 text-[13px] leading-relaxed text-muted-foreground">
            Trinetra runs beside the systems already in place. Adopt the cameras of an existing
            VMS, keep a third-party analytics engine in the loop, and push every Trinetra
            detection back out to the tools a team already watches.
          </p>
        </div>

        {/* Said once, plainly: nothing on this page reaches a server yet. */}
        <p className="border-t border-border bg-muted px-4 py-2 text-[11px] text-muted-foreground">
          Preview build — connectors are configured here but not yet executed on this node.
          Settings are held in the browser and cleared on reload.
        </p>
      </div>

      <PageTabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'vms' && (
        <div className="space-y-3">
          {/* How it works — three cards, because the question every customer asks first
              is what Trinetra will change about the system they already run. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-xl border border-border bg-card p-4 shadow-card">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-light text-[11px] font-semibold text-brand">
                      {i + 1}
                    </span>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="mt-2.5 text-[13px] font-semibold text-foreground">{s.title}</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              );
            })}
          </div>

          <ConnectorGrid
            connectors={VMS_CONNECTORS}
            connections={connections}
            onChange={setConnection}
            canEdit={canEdit}
          />

          <p className="rounded-xl border border-border bg-card px-4 py-3 text-[12px] text-muted-foreground shadow-card">
            Not on the list? <span className="font-medium text-foreground">Custom REST / RTSP</span>{' '}
            covers any system that can describe its cameras over HTTP, and{' '}
            <button
              onClick={() => setTab('import')}
              className="font-medium text-brand underline-offset-2 hover:underline"
            >
              Camera Import
            </button>{' '}
            handles the ones that cannot.
          </p>
        </div>
      )}

      {tab === 'import' && <CameraImport canEdit={canEdit} bulkUploadPath={bulkUploadPath} />}

      {tab === 'analytics' && (
        <div className="space-y-3">
          <ConnectorGrid
            connectors={ANALYTICS_CONNECTORS}
            connections={connections}
            onChange={setConnection}
            canEdit={canEdit}
          />
          <EventBridge canEdit={canEdit} />
        </div>
      )}
    </div>
  );
}
